import { useQuery } from "@tanstack/react-query";
import { ethers } from "ethers";
import { usePolygonClient } from "./usePolygonClient";
import { useAuth } from "./useAuth";
import { useNetworkStore } from "@/stores/useNetworkStore";
import { isMainnetChainId } from "@/config/activeChain";

interface WalletBalances {
  /** USDC balance (6-decimal token) */
  usdc: number;
  /** USDT balance (6-decimal token) */
  usdt: number;
  /** Native POL balance in POL units */
  pol: number;
  /** POL price in USD (null if fetch failed, or on a testnet) */
  polPriceUsd: number | null;
  /** Total value in USD: usdt + pol * polPrice (legacy USDC excluded) */
  totalUsd: number;
}

async function fetchPolPrice(): Promise<number | null> {
  try {
    const res = await fetch(
      "https://api.coingecko.com/api/v3/simple/price?ids=polygon-ecosystem-token&vs_currencies=usd",
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data["polygon-ecosystem-token"]?.usd ?? null;
  } catch {
    return null;
  }
}

export function useWalletBalances() {
  const polygonClient = usePolygonClient();
  const { session } = useAuth();
  const walletAddress = session?.walletAddress;
  // Test POL is worth nothing. Pricing it at the mainnet rate put a made-up
  // dollar figure in the testnet "Total", so a testnet gets no price at all.
  // The chain is frozen per page load, so it needs no place in the key.
  const mainnet = isMainnetChainId(useNetworkStore((s) => s.chainId));

  return useQuery<WalletBalances>({
    queryKey: ["wallet-balances", walletAddress],
    enabled: !!polygonClient && !!walletAddress,
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async () => {
      const addr = walletAddress!;
      const client = polygonClient!;

      const [usdc, usdt, polRaw, polPriceUsd] = await Promise.all([
        client.getTokenBalance("USDC", addr),
        client.getTokenBalance("USDT", addr),
        client.signer.provider!.getBalance(addr),
        mainnet ? fetchPolPrice() : Promise.resolve(null),
      ]);

      const pol = Number(ethers.formatEther(polRaw));
      const polUsd = polPriceUsd != null ? pol * polPriceUsd : 0;

      return {
        usdc,
        usdt,
        pol,
        polPriceUsd,
        totalUsd: usdt + polUsd,
      };
    },
  });
}
